import React, { useState, useEffect } from "react";
import { useVenue } from "@/components/shared/VenueContext";
import { createIncident, updateIncident, getIncidents } from "@/components/services";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import StatusBadge from "@/components/shared/StatusBadge";
import EmptyState from "@/components/shared/EmptyState";
import IncidentForm from "@/components/incidents/IncidentForm";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, AlertTriangle, Pencil, Bot } from "lucide-react";
import moment from "moment";

const CAT_LABELS = {
  food_safety: "Seguretat alimentària",
  equipment_failure: "Avaria d'equip",
  temperature_deviation: "Desviació temperatura",
  cleaning_issue: "Problema de neteja",
  supplier_issue: "Problema proveïdor",
  staff_issue: "Incidència personal",
  other: "Altres",
};

export default function Incidents() {
  const { currentVenue, user, loading: venueLoading } = useVenue();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editingIncident, setEditingIncident] = useState(null);

  useEffect(() => {
    if (currentVenue) loadIncidents();
  }, [currentVenue]);

  const loadIncidents = async () => {
    setLoading(true);
    const items = await getIncidents(currentVenue.id);
    setIncidents(items);
    setLoading(false);
  };

  const handleSave = async (data) => {
    if (editingIncident) {
      await updateIncident(editingIncident.id, data);
    } else {
      await createIncident(currentVenue.id, user, data);
    }
    setEditingIncident(null);
    await loadIncidents();
  };

  if (venueLoading) return <QuickLoader />;

  const filtered = statusFilter === "all"
    ? incidents
    : incidents.filter(i => i.status === statusFilter);

  const openCount = incidents.filter(i => i.status === "open").length;
  const inProgressCount = incidents.filter(i => i.status === "in_progress").length;

  const severityConfig = {
    low: { label: "Baixa", class: "bg-blue-100 text-blue-700 border-blue-200" },
    medium: { label: "Mitjana", class: "bg-amber-100 text-amber-700 border-amber-200" },
    high: { label: "Alta", class: "bg-orange-100 text-orange-800 border-orange-200" },
    critical: { label: "Crítica", class: "bg-red-100 text-red-800 border-red-200" },
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Incidències"
        subtitle={openCount > 0 ? `⚠ ${openCount} obertes · ${inProgressCount} en procés` : "Tot en ordre"}
        actions={
          <Button
            onClick={() => { setEditingIncident(null); setShowForm(true); }}
            className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2 rounded-xl"
          >
            <Plus className="w-4 h-4" /> Nova incidència
          </Button>
        }
      />

      {/* Status filter — scrollable on mobile */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {[
          { key: "all", label: `Totes (${incidents.length})` },
          { key: "open", label: `Obertes (${openCount})` },
          { key: "in_progress", label: `En procés (${inProgressCount})` },
          { key: "closed", label: `Tancades (${incidents.filter(i => i.status === "closed").length})` },
        ].map(({ key, label }) => (
          <Button
            key={key}
            variant={statusFilter === key ? "default" : "outline"}
            size="sm"
            onClick={() => setStatusFilter(key)}
            className={`flex-shrink-0 ${statusFilter === key ? "bg-[#7B2D3F] hover:bg-[#5B1D2F]" : ""}`}
          >
            {label}
          </Button>
        ))}
      </div>

      {/* Incidents list */}
      {loading ? (
        <QuickLoader />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={AlertTriangle}
          title="Sense incidències"
          description="No hi ha incidències amb aquest filtre."
          action={
            <Button onClick={() => setShowForm(true)} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2">
              <Plus className="w-4 h-4" /> Crear incidència
            </Button>
          }
        />
      ) : (
        <div className="space-y-3">
          {filtered.map((inc) => {
            const sev = severityConfig[inc.severity] || severityConfig.medium;
            const statusColors = {
              open: "border-l-amber-400",
              in_progress: "border-l-blue-400",
              closed: "border-l-gray-300",
            };
            return (
              <Card key={inc.id} className={`p-4 sm:p-5 border-0 shadow-sm hover:shadow-md transition-all border-l-4 ${statusColors[inc.status] || "border-l-gray-200"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${sev.class}`}>
                        {sev.label}
                      </span>
                      <StatusBadge status={inc.status} />
                      <span className="text-xs text-gray-400">{CAT_LABELS[inc.category] || inc.category}</span>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1">{inc.title}</h3>
                    {inc.description && (
                      <p className="text-sm text-gray-500 mb-2 line-clamp-2">{inc.description}</p>
                    )}
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <span>{moment(inc.date).format("DD/MM/YYYY")}</span>
                      {inc.responsible_name && <><span>·</span><span>{inc.responsible_name}</span></>}
                    </div>
                    {inc.corrective_action && (
                      <div className="mt-2 p-2.5 bg-blue-50 rounded-xl">
                        <p className="text-xs font-semibold text-blue-800 mb-0.5">✓ Acció correctiva:</p>
                        <p className="text-xs text-blue-700">{inc.corrective_action}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Suggeriment IA"
                      onClick={() => {
                        const q = `Incidència: "${inc.title}". Categoria: ${CAT_LABELS[inc.category] || inc.category}. Gravetat: ${inc.severity}. ${inc.description ? `Descripció: ${inc.description}.` : ""} Suggereix possibles causes, acció correctiva i mesures preventives.`;
                        window.dispatchEvent(new CustomEvent("open-assistant", { detail: { question: q } }));
                      }}
                      className="text-[#7B2D3F] hover:bg-[#7B2D3F]/10 gap-1"
                    >
                      <Bot className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setEditingIncident(inc); setShowForm(true); }}
                      className="text-gray-400 hover:text-gray-700 gap-1"
                    >
                      <Pencil className="w-4 h-4" />
                      <span className="hidden sm:inline text-xs">Editar</span>
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <IncidentForm
        open={showForm}
        onClose={() => { setShowForm(false); setEditingIncident(null); }}
        onSave={handleSave}
        venueId={currentVenue?.id}
        user={user}
        editData={editingIncident}
      />
    </div>
  );
}